import { useQueryClient } from '@tanstack/vue-query'
import { storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'
import { getChatbotSessionHistoryQuery, streamChatbotResponse } from '@/services/api/chatbot.api'
import { useChatbotStore } from '@/stores/chatbot.store'

const CACHE_KEY_CHATBOT = 'chatbot'

// Module level rather than component level so that an unsent draft, and an
// in-flight response, survive the conversation being moved between the layout
// sidebar and the full chatbot page.
const draftMessage = ref('')

let streamAbortController: AbortController | undefined

export function teardownChatbotConversation() {
  draftMessage.value = ''
  streamAbortController?.abort()
  streamAbortController = undefined
}

/**
 * Drives a chatbot conversation. The conversation itself (turns, selected
 * session, streaming state) lives in the chatbot store, so every mounted
 * consumer - the sidebar and the full page - shares a single conversation.
 */
export function useChatbotConversation() {
  const queryClient = useQueryClient()

  const chatbotStore = useChatbotStore()

  const {
    chatTurns,
    hasChatTurns,
    isStreaming,
    selectedSessionId,
    streamError,
    streamingStatus,
  } = storeToRefs(chatbotStore)

  const {
    appendChatTurn,
    appendLatestAssistantContent,
    setChatTurns,
    setIsStreaming,
    setSelectedSessionId,
    setStreamError,
    setStreamingStatus,
  } = chatbotStore

  const {
    data: sessionHistory,
    isFetching: isSessionHistoryFetching,
  } = getChatbotSessionHistoryQuery(selectedSessionId, {
    enabled: computed(() => !!selectedSessionId.value),
  })

  watch(
    () => sessionHistory.value,
    (newVal) => {
      if (!newVal) {
        return
      }

      // Don't overwrite turns if we're actively streaming (composing a new message)
      // The streaming response will update turns via appendLatestAssistantContent
      if (isStreaming.value) {
        return
      }

      setChatTurns([...newVal.turns])
    },
  )

  const canSendMessage = computed(() => {
    return !!draftMessage.value.trim() && !isStreaming.value
  })

  async function sendMessage() {
    if (!canSendMessage.value) {
      return
    }

    if (!selectedSessionId.value) {
      setSelectedSessionId(crypto.randomUUID())
    }

    const message = draftMessage.value.trim()
    draftMessage.value = ''
    setStreamError('')
    setStreamingStatus('')

    appendChatTurn({
      content: message,
      role: 'user',
      timestamp: new Date().toISOString(),
    })

    appendChatTurn({
      content: '',
      role: 'assistant',
      timestamp: new Date().toISOString(),
    })

    setIsStreaming(true)
    streamAbortController?.abort()
    const requestAbortController = new AbortController()
    streamAbortController = requestAbortController

    try {
      await streamChatbotResponse({
        onContent: (chunk) => {
          appendLatestAssistantContent(chunk)
          setStreamingStatus('')
        },
        onDone: async () => {
          setStreamingStatus('')
          await queryClient.invalidateQueries({ queryKey: [CACHE_KEY_CHATBOT] })
        },
        onStatus: (status) => {
          setStreamingStatus(status)
        },
        request: {
          message,
          sessionId: selectedSessionId.value,
        },
        signal: requestAbortController.signal,
      })
    }
    catch (error) {
      const isAbortError = error instanceof DOMException && error.name === 'AbortError'

      if (!isAbortError && streamAbortController === requestAbortController) {
        setStreamError((error as Error).message || 'Unable to stream chatbot response')
      }
    }
    finally {
      const hasNewerActiveRequest = streamAbortController !== undefined && streamAbortController !== requestAbortController

      setIsStreaming(hasNewerActiveRequest)

      if (!hasNewerActiveRequest) {
        setStreamingStatus('')
      }

      if (streamAbortController === requestAbortController) {
        streamAbortController = undefined
      }
    }
  }

  function stopStreaming() {
    streamAbortController?.abort()
  }

  return {
    chatTurns,
    draftMessage,
    hasChatTurns,
    isSessionHistoryFetching,
    isStreaming,
    sendMessage,
    stopStreaming,
    streamError,
    streamingStatus,
  }
}
